import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useAdapterMode } from '../adapters/AdapterProvider'
import { useGuardedNavigate } from '../state/NavGuard'

interface NavItem {
  to: string
  labelKey: 'nav.templates' | 'nav.blocks' | 'nav.rules' | 'nav.printers'
  icon: ReactNode
}

const ITEMS: NavItem[] = [
  {
    to: '/templates',
    labelKey: 'nav.templates',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 10h18" />
      </svg>
    ),
  },
  {
    to: '/blocks',
    labelKey: 'nav.blocks',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    to: '/rules',
    labelKey: 'nav.rules',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
        <path d="M4 6h16" />
        <path d="M4 12h10" />
        <path d="M4 18h6" />
        <circle cx="18" cy="12" r="2" />
      </svg>
    ),
  },
  {
    to: '/printers',
    labelKey: 'nav.printers',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
        <path d="M6 9V4h12v5" />
        <rect x="3" y="9" width="18" height="9" rx="1" />
        <path d="M6 14h12" />
        <path d="M6 17h4" />
        <circle cx="17.5" cy="12" r="0.75" fill="currentColor" />
      </svg>
    ),
  },
]

export function LeftNav() {
  const { t } = useTranslation()
  const guardedNavigate = useGuardedNavigate()
  const mode = useAdapterMode()

  return (
    <aside className="flex w-56 shrink-0 flex-col border-r border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <div className="flex h-14 items-center gap-2 border-b border-stone-200 px-4 dark:border-stone-800">
        <div className="h-6 w-6 rounded bg-stone-900 dark:bg-stone-100" />
        <span className="font-semibold tracking-tight">{t('nav.appName')}</span>
      </div>
      <nav className="flex flex-col gap-1 p-3">
        {ITEMS.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={(e) => {
              // Modified clicks (cmd/ctrl/shift/alt, non-primary button) keep
              // the native new-tab/new-window behavior — don't intercept.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
              e.preventDefault()
              void guardedNavigate(item.to)
            }}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
                isActive
                  ? 'bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900'
                  : 'text-stone-600 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-800',
              ].join(' ')
            }
          >
            {item.icon}
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
      {mode === 'demo' && (
        <div className="mt-auto p-3 text-xs text-stone-500 dark:text-stone-500">
          {t('nav.phaseBadge')}
        </div>
      )}
    </aside>
  )
}
